<template>
    <fe-stepper-filter
        title="Program Evaluation" style="height:620px;"
        @apply="next" @close="$emit('close'); clearFilters()"
        :applyDisabled="!isNextValid"
        :applyBtnText="view.id == 'population' ? 'Next' : 'Search'"
    >
        <template #toolbar>
            <stepper-navigation :items="navs" v-model="view" :nextDisabled="!isNextValid"/>
        </template>

        <template #footer>
            <div class="d-flex" style="width: 100%;">
                <div class="mt-3 ml-4" v-safe-html="searchDisplay"></div>
                <v-spacer/>
                <fe-btn v-if="view.id == 'population' && isSearchValid" usage="secondary" @click="emit">Search</fe-btn>
            </div>
        </template>

        <keep-alive>
            <template v-if="view.id=='population'">
                <student-stepper-panel
                    ref="studentStepper"
                    v-model="population"
                    :surfaceFirstSelections="surfaceFirstSelections"
                    multipleYears
                    style="height: 480px;"
                    @selected="checkValid"
                    @searchDisplayChange="searchDisplayPopulation = $event"
                />
            </template>

            <template v-if="view.id=='data'">
                <sub-category-stepper-panel
                    v-model="assessment"
                    :surfaceFirstSelections="surfaceFirstSelections"
                    @selected="checkValid"
                    multipleWindows
                    :showYears="false"
                    :allowScoreDetails="false"
                    style="height: 480px;"
                    @searchDisplayChange="searchDisplayData = $event"
                />
            </template>
        </keep-alive>

        <embargoed-data-agreement
            v-if="showEmbargoAcceptDialog"
            :embargoes="embargoes"
            @accept="onAcceptEmbargo"
        />
    </fe-stepper-filter>
</template>

<script>
    import * as Util from './Util'
    import EmbargoedDataAgreement from '@/components/common/EmbargoedDataAgreement'
    import StudentStepperPanel from '@/components/common/stepper/StudentStepperPanel'
    import AssessmentStepperPanel from '@/components/common/stepper/AssessmentStepperPanel'
    import IncidentStepperPanel from '@/components/common/stepper/IncidentStepperPanel'
    import StepperNavigation from './StepperNavigation'
    import SubCategoryStepperPanel from '../../common/stepper/SubCategoryStepperPanel.vue'
    import hybridMixin from "./mixins/hybrids";

    export default {
        name: 'ProgramEvaluationStepper',

        mixins: [hybridMixin],

        components: {
            EmbargoedDataAgreement,
            StudentStepperPanel,
            AssessmentStepperPanel,
            IncidentStepperPanel,
            StepperNavigation,
            SubCategoryStepperPanel
        },

        props: {
            state: {
                type: Object,
                required: false
            },
            byId: {
                type: Boolean,
                default: true
            },
            value: {
                type: Object,
                default: null
            },
            surfaceFirstSelections: {
                type: Boolean,
                default: false
            },
        },
        watch: {
            'view.id'(v, prevVal) {
                if (prevVal == 'population') {
                    this.population = {
                        ...this.population,
                        ...this.$refs.studentStepper.getSubmitParams(true) // only want the filters - this.population holds school year, school et al. already
                    }
                }

                this.checkValid()
            },
            population: {
                deep: true,
                handler(v) {
                    this.format()
                }
            },
        },
        data() {
            return {
                population: {
                    school_year_id: [],
                    school_id: [],
                    grade_id: [],
                    advancedFilters: {
                        student_active_flag: 1
                    },
                },
                assessment: {
                    data_point_type_id: null,
                    category_id: null,
                    sub_category_id: null,
                    data_point_name_id: [],
                },
                formattedParams: {},
                view: {
                    id: ''
                },
                navs: [{
                    id: 'population',
                    name: 'Student Population'
                }, {
                    id: 'data',
                    name: 'Data Types'
                }],
                searchDisabled: true,
                allowNext: false,
                searchDisplayPopulation: '<b>Select School Years</b>',
                searchDisplayData: '<b>Select Assessment Group</b>',
                showEmbargoAcceptDialog: false,
                embargoes: [],
            }
        },
        computed: {
            formValid() {
                return false
            },
            schoolYearId() {
                return this.population.school_year?.id
            },
            searchDisplay() {
                if (this.view.id == 'population') {
                    return this.searchDisplayPopulation
                } else {
                    return this.searchDisplayData
                }
            },
            isPopulationValid() {
                if (!this.population?.school_year_id?.length || !this.population?.school_id?.length || !this.population?.grade_id?.length) {
                    return false
                } else {
                    return true
                }
            },
            isDataValid() {
                return (this.assessment?.data_point_type_id && this.assessment?.sub_category_id && this.assessment?.data_point_name_id?.length)
            },
            isNextValid() {
                return (this.view.id == 'population' && this.isPopulationValid) || (this.view.id != 'population' && this.isSearchValid)
            },
            isSearchValid() {
                return this.isPopulationValid && this.isDataValid
            },
        },

        mounted() {
            this.view = this.navs[0]
            this.loadBase()

            this.population = this.$_.merge(
                this.population,
                this.value?.populationParams,
                {
                    advancedFilters: {
                        ...this.value?.advancedFilterParams,
                        // For whatever reason, this ell value is different on backend/FE model
                        ell: this.value?.searchParams?.ell_flag || undefined,
                        demographics: Util.buildDemographicsObject(this.value?.advancedFilterParams),
                    }
                }
            )

            this.assessment = this.$_.merge(this.assessment, this.value?.assessmentParams)
        },
        methods: {
            checkValid(item) {
                if (this.view.id == 'population') {
                    this.searchDisabled = this.population?.school_year_id?.length > 0 ? false : true
                } else if (this.view.id == 'data') {
                    if (this.assessment?.sub_category_id) {
                        this.searchDisabled = false
                    } else {
                        this.searchDisabled = true
                    }
                }
            },

            clearFilters() {
                this.population={}
                this.assessment={}
                this.incident={}
                this.view='population'
            },

            next() {
                switch (this.view.id) {
                    case 'population':
                        this.view = this.navs[1]
                        break
                    default:
                        this.emit()
                        break
                }
            },

            loadBase() {
                this.dialog=true

                // hybrid demographics
                this.loadEll()
                this.loadEthnicities()
                this.loadGenders()
                this.loadDisabilities()
                this.loadMealStatus()
            },

            format() {
                let assessment = this.assessment

                // population already has _id on everything thanks to StudentStepper naming conventions
                let p = this.population
                if (p.advancedFilters.disability_type_id && p.advancedFilters.disability_type_id[0].allIds) {
                    p.advancedFilters.disability_type_id = p.advancedFilters.disability_type_id[0].allIds
                    p.advancedFilters.disability_code = 'ALLDIS' // use for stepper display if All Disabilities was chosen
                }
                if (p.advancedFilters.meal_status_id && p.advancedFilters.meal_status_id[0].allIds) {
                    p.advancedFilters.meal_status_id = p.advancedFilters.meal_status_id[0].allIds
                    p.advancedFilters.status_code = 'ALLSTATUS' // use for stepper display if Any Status was chosen
                }
                if (p.advancedFilters.intervention_level && p.advancedFilters.intervention_level[0].allIds) {
                    p.advancedFilters.intervention_level = p.advancedFilters.intervention_level[0].allIds
                    p.advancedFilters.intervention_code = 'ALLINT' // use for stepper display if All Interventions was chosen
                }

                // HYBRID DEMOS BACKEND PAYLOAD
                // make sure we're sending values to BE for queries (not groups or display_names)
                // ETHNICITY
                if (p.advancedFilters.ethnicity) {
                    let ethnicities = ''
                    let selected = p.advancedFilters.ethnicity.split(',')
                    this.ethnicity.forEach(x => {
                        if (selected.indexOf(x.display_name) > -1 || selected.indexOf(x.value) > -1) {
                            if (x.display_name_group) {
                                x.display_name_group.forEach(n => {
                                    if (ethnicities.indexOf(n) === -1) {
                                        ethnicities += n + ',' // add name if it doesn't exist in list
                                    }
                                })
                            } else {
                                ethnicities += x.value + ','
                            }
                        }
                    })
                    // remove duplicates
                    let tmp = ethnicities.split(',')
                    tmp = [...new Set(tmp)]
                    p.advancedFilters.ethnicity = tmp.slice(0, -1).join(',')
                }
                // GENDER
                if (p.advancedFilters.gender) {
                    let genders = ''
                    let selected = p.advancedFilters.gender.split(',')
                    this.gender.forEach(x => {
                        if (selected.indexOf(x.display_name) > -1 || selected.indexOf(x.value) > -1) {
                            if (x.display_name_group) {
                                x.display_name_group.forEach(n => {
                                    if (genders.indexOf(n) === -1) {
                                        genders += n + ',' // add name if it doesn't exist in list
                                    }
                                })
                            } else {
                                genders += x.value + ','
                            }
                        }
                    })
                    // remove duplicates
                    let tmp = genders.split(',')
                    tmp = [...new Set(tmp)]
                    p.advancedFilters.gender = tmp.slice(0, -1).join(',')
                }
                // DISABILITY
                if (
                    p.advancedFilters.disability_type_id &&
                    p.advancedFilters.disability_code !== 'ALLDIS' &&
                    p.advancedFilters.disability_code !== 'NODIS' &&
                    p.advancedFilters.disability_type_id[0].id !== -1 &&
                    p.advancedFilters.disability_type_id[0].id !== 0
                ) {
                    let disabilities = ''
                    let selected = p.advancedFilters.disability_type_id.split(',')
                    this.disability.forEach(x => {
                        if (selected.indexOf(x.id.toString()) > -1) {
                            if (x.display_name_group) {
                                disabilities += x.display_name_group.toString() + ','
                            } else {
                                disabilities += x.id + ','
                            }
                        }
                    })
                    // remove duplicates
                    let tmp = disabilities.split(',')
                    tmp = [...new Set(tmp)]
                    p.advancedFilters.disability_type_id = tmp.slice(0, -1).join(',')
                } else if (p.advancedFilters.disability_code === 'ALLDIS' || (p.advancedFilters.disability_type_id && p.advancedFilters.disability_type_id[0].code === 'ALLDIS')) {
                    p.advancedFilters.disability_code = 'ALLDIS'
                }
                // ELP LEVEL
                if (p.advancedFilters.ell_level_id) {
                    let levels = []
                    let selected = p.advancedFilters.ell_level_id
                    this.elpLevel.forEach(x => {
                        if (selected.indexOf(x.id) > -1) {
                            if (x.display_name_group) {
                                levels = levels.concat(x.display_name_group)
                            } else {
                                levels.push(x.id)
                            }
                        }
                    })
                    levels = [...new Set(levels)] // remove duplicates
                    p.advancedFilters.ell_level_id = levels.slice().join(',') // .slice.join prevents infinite loop
                }
                // MEAL STATUS
                if (
                    p.advancedFilters.meal_status_id &&
                    p.advancedFilters.status_code !== 'ALLSTATUS' &&
                    p.advancedFilters.status_code !== 'NOSTATUS' &&
                    p.advancedFilters.meal_status_id[0].id !== -1 &&
                    p.advancedFilters.meal_status_id[0].id !== 0
                ) {
                    let status = ''
                    let selected = p.advancedFilters.meal_status_id.split(',')
                    this.mealStatus.forEach(x => {
                        if (selected.indexOf(x.id.toString()) > -1) {
                            if (x.display_name_group) {
                                status += x.display_name_group.toString() + ','
                            } else {
                                status += x.id + ','
                            }
                        }
                    })
                    // remove duplicates
                    let tmp = status.split(',')
                    tmp = [...new Set(tmp)]
                    p.advancedFilters.meal_status_id = tmp.slice(0, -1).join(',')
                }

                let a = {
                    school_year_id: p.school_year_id,
                    category_id: assessment?.category_id || [],
                    data_point_type_id: assessment?.data_point_type_id,
                    sub_category_id: assessment?.sub_category_id,
                    data_point_name_id: assessment?.data_point_name_id || [],
                }

                return {
                    population: p,
                    assessment: (a.category_id.length>0 || a.data_point_type_id || a.sub_category_id) ? a : null
                }
            },

            emit() {
                let params = this.format()
                if (this.$hasSecurity('VIEW_EMBARGOED_DATA') && params.assessment) {
                    this.$axios.get(`targetScores.php?action=get&property=data_point_embargoes&` + this.$objectToParams(params.assessment)).then(res => {
                        if (res.data.data_point_embargo?.length) {
                            this.showEmbargoAcceptDialog = true
                            this.embargoes = res.data.data_point_embargo
                        } else {
                            this.doSearch()
                        }
                    })
                } else {
                    this.doSearch()
                }
            },

            onAcceptEmbargo() {
                this.showEmbargoAcceptDialog = false
                this.doSearch()
            },

            doSearch() {
                let assessment = this.assessment
                let opts = this.format()

                if (this.state?.windowUUID) {
                    this.$store.commit('global/removeDockedWindow', this.$store.getters['global/dockableWindows'].find(w => w.uuid === this.state?.windowUUID))
                }

                Util.doProgramEvaluation(this, opts, 'district', null, { ...this.state, isDirty: true })
                this.$emit('close')
            }
        },
    }
</script>

<style lang="scss" scoped>

</style>
